import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { resolveApiBaseUrl } from '../config/apiConfig';
import { useAuth } from './AuthContext';

const SovereignStateContext = createContext(null);

export const SovereignStateProvider = ({ children }) => {
  const { user, userRole, claims, claimsLoading } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [endpointUnavailable, setEndpointUnavailable] = useState(false);
  const [lastValidTimestamp, setLastValidTimestamp] = useState(null);
  const intervalRef = useRef(null);
  const hasFinanceAccess = userRole === 'admin'
    || userRole === 'operator'
    || userRole === 'radar_operator'
    || claims?.admin === true
    || claims?.operator === true
    || claims?.radar_operator === true;

  /**
   * The auth context hands back a new user object on every render, so depending
   * on that object made fetchState a new function each render, which re-ran the
   * polling effect, which fetched again — an unbounded request loop against the
   * finance endpoint. Depend on the uid instead and read the live user through a
   * ref, so fetchState only changes when access actually changes.
   */
  const userId = user?.uid || null;
  const canFetchState = Boolean(userId) && hasFinanceAccess;
  const userRef = useRef(user);
  userRef.current = user;

  const getUrl = (path = '') => `${resolveApiBaseUrl() || ''}/api/finance/state-vector${path}`;

  const fetchState = useCallback(async () => {
    if (claimsLoading) return;
    const currentUser = userRef.current;
    if (!canFetchState || typeof currentUser?.getIdToken !== 'function') {
      setLoading(false);
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getUrl(), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`Server returned HTTP ${res.status}`);
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed to fetch state vector');
      
      setState(data.vector);
      setLastValidTimestamp(data.vector.timestamp);
      setError(null);
      setEndpointUnavailable(false);
    } catch (err) {
      console.error('[SovereignStateProvider] Fetch error:', err.message);
      setError(err.message);
      setEndpointUnavailable(true);
      // We explicitly retain the previous state object to allow the UI to freeze
      // consistently rather than empty/crashed.
    } finally {
      setLoading(false);
    }
  }, [canFetchState, claimsLoading]);

  const refresh = useCallback(async () => {
    if (!canFetchState || typeof userRef.current?.getIdToken !== 'function') return;

    setRefreshing(true);
    try {
      const token = await userRef.current.getIdToken();
      const res = await fetch(getUrl('/trigger'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`Server returned HTTP ${res.status}`);
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed to trigger compute');
      
      setState(data.vector);
      setLastValidTimestamp(data.vector.timestamp);
      setError(null);
      setEndpointUnavailable(false);
    } catch (err) {
      console.error('[SovereignStateProvider] Refresh error:', err.message);
      setError(err.message);
      setEndpointUnavailable(true);
    } finally {
      setRefreshing(false);
    }
  }, [canFetchState]);

  // Poll state vector periodically (30s) when visible
  useEffect(() => {
    fetchState(); // initial load

    const startInterval = () => {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(fetchState, 30000);
    };

    const stopInterval = () => {
      clearInterval(intervalRef.current);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        fetchState();
        startInterval();
      }
    };

    startInterval();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchState]);

  // Derive values and diagnostics
  const mission = state?.reasoning?.mission || null;
  const readiness = state?.reasoning?.executionReadiness || null;
  const freshness = state?.reality?.freshness || null;

  const lastUpdated = state?.timestamp || lastValidTimestamp || null;
  const confidence = state?.confidence ?? 1.0;
  const stateVersion = state?.version ?? '1.2.0';

  const timestampAgeMs = state?.timestamp ? Date.now() - Date.parse(state.timestamp) : Infinity;
  const snapshotExpired = !Number.isFinite(timestampAgeMs) || timestampAgeMs > 10 * 60 * 1000;

  const isStale = endpointUnavailable || snapshotExpired || (freshness
    ? Object.values(freshness).some(f => f.status === 'STALE' || f.status === 'UNAVAILABLE')
    : false);

  const staleFeeds = [];
  if (endpointUnavailable) {
    staleFeeds.push('state-vector-endpoint');
  }
  if (snapshotExpired) {
    staleFeeds.push('state-vector-age');
  }
  if (freshness) {
    Object.keys(freshness).forEach(k => {
      if (freshness[k].status !== 'OK') {
        staleFeeds.push(k);
      }
    });
  }

  // Suppress all recommendations when data is stale/degraded to maintain operational integrity
  const recommendations = isStale ? [] : (state?.reasoning?.recommendations || []);

  // Calculate average latency across available feeds
  let latency = 0;
  if (freshness) {
    const feeds = Object.values(freshness);
    const sum = feeds.reduce((acc, f) => acc + (f.latencySeconds || 0), 0);
    latency = feeds.length > 0 ? parseFloat((sum / feeds.length).toFixed(2)) : 0;
  }

  const value = {
    state,
    mission,
    readiness,
    freshness,
    recommendations,
    refresh,
    loading,
    error,
    refreshing,
    lastUpdated,
    isStale,
    staleFeeds,
    endpointUnavailable,
    confidence,
    latency,
    stateVersion
  };

  return (
    <SovereignStateContext.Provider value={value}>
      {children}
    </SovereignStateContext.Provider>
  );
};

export const useSovereignState = () => {
  const context = useContext(SovereignStateContext);
  if (!context) {
    throw new Error('useSovereignState must be used within a SovereignStateProvider');
  }
  return context;
};
